import { Metamodel } from 'app/model/base/model';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { FLOW } from 'app/model/adapters/dataflow/IDataflow';
import { TransformationAdapter } from 'app/model/adapters/dataflow/transformations/base-transformation.adapter';
import { BrowseTransformation } from 'app/model/dataflow/browsetransformation';
import { DataFlowExpressionUtils } from '../expression.util';
import { CdmManifestType, CdmSettings } from 'app/model/dataflow/dataset-settings/cdm-settings';
import { stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { AzureBlobFSLinkedService } from 'app/model/linkedservice/azure-blob-fs.linkedservice';
import { GitHubLinkedService } from 'app/model/linkedservice/github.linkedservice';
import { DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { SqlBaseSettings } from 'app/model/dataflow/dataset-settings/sql-base-settings';
import { DynamicsSettings } from 'app/model/dataflow/dataset-settings/dynamics-settings';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { PluginMetadataProcessService } from 'app/services/plugin-metadata-process.service';
import { CosmosDBStoreType, CosmosSqlSettings } from 'app/model/dataflow/dataset-settings/cosmos-sql-settings';
import { EventHubsSettings } from 'app/model/dataflow/dataset-settings/event-hubs-settings';
import { CosmosMongoSettings } from 'app/model/dataflow/dataset-settings/cosmos-mongo-settings';
import { KafkaSettings } from 'app/model/dataflow/dataset-settings/kafka-settings';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { LakehouseLinkedService } from 'shell-models-lib';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(BrowseTransformation),
    asynchronous: true
})
export class BrowseTransformationAdapter extends TransformationAdapter<BrowseTransformation> {
    public async getDslScript(browseTransformation: BrowseTransformation): Promise<string> {
        const props: string[] = [];
        const datasetSettingsFormat = browseTransformation.datasetSettings?.getFormat();
        if (datasetSettingsFormat === DatasetFormatType.CDM) {
            const cdmSettings = browseTransformation.datasetSettings as CdmSettings;
            props.push(`format: '${DatasetFormatType.CDM}'`);
            if (
                cdmSettings.isLocalEntityReference ||
                cdmSettings.isCustomEntityReference ||
                cdmSettings.manifestType === CdmManifestType.MODEL
            ) {
                if (!cdmSettings.isCustomEntityReference) {
                    props.push(`manifestType: '${cdmSettings.manifestType}'`);
                }
                if (cdmSettings.manifestType === CdmManifestType.MANIFEST) {
                    if (!stringIsNullOrEmpty(cdmSettings.manifestName.expression)) {
                        props.push(
                            `manifestName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                cdmSettings.manifestName
                            )}`
                        );
                    }
                    if (!stringIsNullOrEmpty(cdmSettings.entityPath.expression)) {
                        props.push(
                            `entityPath: ${DataFlowExpressionUtils.wrapExpressionConfigValue(cdmSettings.entityPath)}`
                        );
                    }
                }
                if (!stringIsNullOrEmpty(cdmSettings.subFormatSettings.folderPath.expression)) {
                    props.push(
                        `folderPath: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            cdmSettings.subFormatSettings.folderPath
                        )}`
                    );
                }
                if (!stringIsNullOrEmpty(cdmSettings.subFormatSettings.container.expression)) {
                    props.push(
                        `fileSystem: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            cdmSettings.subFormatSettings.container
                        )}`
                    );
                } else {
                    // TODO: Remove this after fix from backend is made. This shouldn't be required for github LS.
                    props.push(`fileSystem: 'fakeFileSystem'`);
                }
                if (cdmSettings.isCustomEntityReference) {
                    props.push(
                        `corpusPath: ${DataFlowExpressionUtils.wrapExpressionConfigValue(cdmSettings.corpusPath)}`
                    );
                    if (browseTransformation.schemaLinkedService instanceof AzureBlobFSLinkedService) {
                        props.push(`corpusStore: 'adlsgen2'`);
                        props.push(
                            `adlsgen2_fileSystem: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                cdmSettings.container
                            )}`
                        );
                    } else if (browseTransformation.schemaLinkedService instanceof GitHubLinkedService) {
                        props.push(`corpusStore: 'github'`);
                        props.push(
                            `github_repository: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                cdmSettings.repository
                            )}`
                        );
                        props.push(
                            `github_branch: ${DataFlowExpressionUtils.wrapExpressionConfigValue(cdmSettings.branch)}`
                        );
                    }
                }
            } else if (cdmSettings.isStandardEntityReference) {
                props.push(`store: 'local'`);
            }
        } else if (
            browseTransformation.datasetSettings &&
            (datasetSettingsFormat === DatasetFormatType.SqlDB ||
                datasetSettingsFormat === DatasetFormatType.SynapseAnalytics ||
                datasetSettingsFormat === DatasetFormatType.OnPremiseSqlServer)
        ) {
            const datasetSettings = browseTransformation.datasetSettings as SqlBaseSettings;
            props.push(`format: '${datasetSettings.format}'`);
            props.push(`store: 'sqlserver'`);
            TransformationAdapter.appendDatabaseProp(datasetSettings, props);
            if (browseTransformation.parameterBrowse) {
                props.push(
                    `schemaName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(datasetSettings.schemaName)}`
                );
                props.push(
                    `procedureName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                        datasetSettings.storedProcName
                    )}`
                );
                props.push(`parameters: ${browseTransformation.parameterBrowse}`);
            }
        } else if (
            datasetSettingsFormat === DatasetFormatType.Mysql ||
            datasetSettingsFormat === DatasetFormatType.PostgreSql
        ) {
            const source = browseTransformation.datasetSettings.owner as DataFlowSource;
            if (source) {
                props.push(`format: '${source.format}'`);
                props.push(`store: '${browseTransformation.datasetSettings.getFormat()}'`);
                if (browseTransformation.parameterBrowse) {
                    props.push(`schemaName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(source.schemaName)}`);
                    props.push(
                        `procedureName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(source.storedProcName)}`
                    );
                    props.push(`parameters: ${browseTransformation.parameterBrowse}`);
                }
            }
        } else if (
            browseTransformation.datasetSettings instanceof DynamicsSettings &&
            browseTransformation.browseEntity === false
        ) {
            props.push(`browseEntity: ${browseTransformation.browseEntity}`);
            props.push(
                `entity: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    browseTransformation.datasetSettings.tableName
                )}`
            );
        } else if (
            (browseTransformation.datasetSettings instanceof CosmosSqlSettings || browseTransformation.datasetSettings instanceof CosmosMongoSettings)
            && browseTransformation.datasetSettings.storeType === CosmosDBStoreType.Analytical
        ) {
            props.push(`storeType: '${CosmosDBStoreType.Analytical}'`);
            TransformationAdapter.appendDatabaseProp(browseTransformation.datasetSettings, props);
        } else if (datasetSettingsFormat === DatasetFormatType.EventHubs) {
            const eventHubsSettings = browseTransformation.datasetSettings as EventHubsSettings;
            props.push(`store: '${eventHubsSettings.store}'`);
            props.push(`format: '${eventHubsSettings.format.expression}'`);
            props.push(`namespaceName: '${eventHubsSettings.namespaceName}'`);
            props.push(`authenticationType: '${eventHubsSettings.authenticationType}'`);
            props.push(`accessKeyName: '${eventHubsSettings.accessKeyName}'`);
            props.push(`accessKey: '${eventHubsSettings.accessKey}'`);
            props.push(`securityProtocol: '${eventHubsSettings.securityProtocol}'`);
            props.push(`saslMechanism: '${eventHubsSettings.saslMechanism}'`);
        }
        else if (datasetSettingsFormat === DatasetFormatType.Kafka) {
            const kafkaSettings = browseTransformation.datasetSettings as KafkaSettings;
            props.push(`store: '${kafkaSettings.store}'`);
            props.push(`format: '${kafkaSettings.format.expression}'`);
            props.push(`bootstrapServers: '${kafkaSettings.bootstarpsServers}'`);
            props.push(`authenticationType: '${kafkaSettings.authenticationType}'`);
            props.push(`username: '${kafkaSettings.username}'`);
            props.push(`password: '${kafkaSettings.password}'`);
            props.push(`saslMechanism: '${kafkaSettings.saslMechanism}'`);
            props.push(`securityProtocol: '${kafkaSettings.securityProtocol}'`);
        } else if (browseTransformation.schemaLinkedService instanceof LakehouseLinkedService && ['avro', 'cdm','delimited', 'delta', 'json', 'orc', 'parquet'].includes(datasetSettingsFormat)) {
            props.push(`format: '${datasetSettingsFormat}'`);
        } else {
            // TODO: implement non-cdm case
            TransformationAdapter.appendDatabaseProp(browseTransformation.datasetSettings, props);
        }
        if (browseTransformation.propertyMap.size > 0) {
            const pluginMetadataProcessService = await getServiceInstance<PluginMetadataProcessService>(
                this.context.contextData,
                'PluginMetadataProcessService'
            );
            const connectorName = browseTransformation.schemaLinkedService
                ? Metamodel.modelOf(browseTransformation.schemaLinkedService)?.name
                : undefined;
            browseTransformation.propertyMap.forEach((value, key) => {
                if (!stringIsNullOrEmpty(value.expression)) {
                    props.push(
                        `${
                            pluginMetadataProcessService.getResourceName(connectorName, key) ?? key
                        }: ${DataFlowExpressionUtils.wrapExpressionConfigValue(value, value.requiredReturnType)}`
                    );
                }
            });
        }
        const knownProps = props.filter((prop) => !!prop).join(',\n\t');
        const unknownProps = this._getUnknownProps(browseTransformation, knownProps);
        const transformationData = [knownProps, unknownProps].filter((c) => !!c).join(',\n\t');

        return `browse(${transformationData}) ${FLOW} ${browseTransformation.outputStream.qualifiedName}`;
    }
}
